import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SignUpStoreService } from '../../../services/sign-up-store/sign-up-store.service';
import {
  SignUpCompanyData,
  SignUpInformationsData,
} from '../interfaces/sign-up-store.interface';
import { StepperNavigationHelperService } from '../../../../shared/services/behavior/stepper-navigation-helper.service';
import { RecapitulatifReadComponent } from './recapitulatif-read/recapitulatif-read.component';
import {
  ErrorState,
  ErrorStateService,
} from '../../../../rapport/state/error-state.service';
import { Subject, takeUntil } from 'rxjs';
import { CustomerService } from '../../../services/customer/customer.service';
import { SignUpResponse } from '../../../models/sign-up-response.model';
import { Router } from '@angular/router';
import { PLoaderComponent } from '../../../../shared/design-system/atoms/p-loader/p-loader.component';
import { PErrorNotificationComponent } from '../../../../shared/design-system/molecules/p-error-notification/p-error-notification.component';
import { FriendlyCaptchaService } from '../../../services/friendly-captcha/friendly-captcha.service';
import { WidgetHandle } from '@friendlycaptcha/sdk';

@Component({
  selector: 'app-recapitulatif',
  standalone: true,
  imports: [
    RecapitulatifReadComponent,
    CommonModule,
    PLoaderComponent,
    PErrorNotificationComponent,
  ],
  templateUrl: './recapitulatif.component.html',
  styleUrl: './recapitulatif.component.scss',
})
export class RecapitulatifComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly CONFIRMATION_URI = '/inscription/confirmation';
  private readonly CONNEXION_URI = '/connexion';
  private readonly destroy$ = new Subject<void>();
  @ViewChild('captchaContainer') captchaContainer!: ElementRef<HTMLElement>;

  mesInformations!: SignUpInformationsData;
  monEntreprise!: SignUpCompanyData;
  isLoading = false;
  errorMessage: string | null = null;
  errorStatus: number | null = null;

  captchaToken: string | null = null;
  private widget: WidgetHandle | null = null;

  constructor(
    private signUpStoreService: SignUpStoreService,
    private stepperNavigationHelper: StepperNavigationHelperService,
    private errorStateService: ErrorStateService,
    private readonly customerService: CustomerService,
    private readonly router: Router,
    private readonly friendlyCaptchaService: FriendlyCaptchaService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  ngOnInit(): void {
    this.mesInformations = this.signUpStoreService.getInformationsSnapshot();
    this.monEntreprise = this.signUpStoreService.getCompanySnapshot();
    this.errorStateService.errorState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((errorState: ErrorState) => {
        this.errorMessage = errorState.message;
        this.errorStatus = errorState.status;
      });
    this.stepperNavigationHelper
      .getFinalize()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        await this.onFinalConfirmed();
      });
    this.stepperNavigationHelper.emitDisableGoToStep(true);
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.widget = this.friendlyCaptchaService.createWidget(
      this.captchaContainer.nativeElement,
    );

    this.widget.addEventListener('frc:widget.complete', (event) => {
      this.captchaToken = event.detail.response;
      this.stepperNavigationHelper.emitDisableGoToStep(false);
    });
    this.widget.addEventListener('frc:widget.error', () => {
      this.clearCaptchaToken();
    });
    this.widget.addEventListener('frc:widget.expire', () => {
      this.clearCaptchaToken();
    });
  }

  ngOnDestroy(): void {
    this.errorStateService.clearError();
    this.widget?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUpdateStep(index: number) {
    this.stepperNavigationHelper.emitGoToStep(index);
  }

  onNotificationClick(): void {
    if (!this.isLoading) {
      this.errorStateService.clearError();
    }
  }

  async onFinalConfirmed() {
    if (!this.captchaToken) {
      return;
    }

    const state = this.signUpStoreService.getSnapshot();
    this.isLoading = true;
    let signUpResponse: SignUpResponse | null;
    try {
      signUpResponse = await this.customerService.addCustomer(
        state,
        this.captchaToken,
      );
    } finally {
      this.isLoading = false;
    }

    this.resetCaptcha();

    if (!signUpResponse) {
      return;
    }

    if (signUpResponse.headers.message) {
      await this.router.navigate([this.CONNEXION_URI], {
        queryParams: { message: signUpResponse.headers.message },
        replaceUrl: true,
      });
    } else {
      await this.router.navigate([this.CONFIRMATION_URI], { replaceUrl: true });
    }
  }

  private resetCaptcha(): void {
    this.clearCaptchaToken();
    this.widget?.reset();
  }

  private clearCaptchaToken(): void {
    this.captchaToken = null;
    this.stepperNavigationHelper.emitDisableGoToStep(true);
  }
}
